import React, { useState } from 'react';
import {
    Typography,
    Button,
    Container,
    Box,
    Grid,
    Stack,
    useMediaQuery,
    Drawer,
    List,
    ListItemButton,
    ListItemText,
} from '@mui/material';
import { createTheme, ThemeProvider, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

const SERVER = process.env.REACT_APP_SERVER;
const homeFileUrl = (fileName) => `${SERVER}/api/upload/homeFiles/${fileName}`;

// --- 0. 이미지 자산 (경로를 채워주세요) ---
const assets = {
    logo: homeFileUrl('GARAM_03.png'), // 로고 이미지
    heroBackground: homeFileUrl('main_img.png'), // 메인 배너 (여성)
    card1: homeFileUrl('main_card1.svg'), // 청년 상담 (카드 1)
    card2: homeFileUrl('main_card2.svg'), // 다이어트 (카드 2)
    card3: homeFileUrl('main_card3.svg'), // 시니어 (카드 3)
    appShowcase: homeFileUrl('main_section2_1.svg'), // 말랑포켓 앱 + 차트 이미지
    chart: homeFileUrl('main_section2_2.svg'),
    teamMeeting: homeFileUrl('main_section3.svg'), // 하단 팀 회의 사진
    footerLogo: homeFileUrl('GARAM_03.png'), // 푸터용 화이트 로고
    icon1: homeFileUrl('main_icon1.svg'), // 아이콘 1
    icon2: homeFileUrl('main_icon2.svg'), // 아이콘 2
    icon3: homeFileUrl('main_icon3.svg'), // 아이콘 3
};

// --- 1. 테마 설정 (반응형 폰트 적용) ---
let theme = createTheme({
    palette: {
        primary: { main: '#005a8d' }, // 가람마인드 네이비 블루
        secondary: { main: '#e3f2fd' },
        text: { primary: '#1a1a1a', secondary: '#555555' },
        background: { default: '#ffffff' },
    },
    typography: {
        fontFamily: '"Pretendard", "Noto Sans KR", sans-serif',
        h3: { fontWeight: 700, lineHeight: 1.3 },
        h4: { fontWeight: 700, lineHeight: 1.35 },
        h5: { fontWeight: 600 },
        subtitle1: { lineHeight: 1.6 },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: { borderRadius: '50px', padding: '10px 24px', fontWeight: 600 },
            },
        },
        MuiContainer: {
            defaultProps: { maxWidth: 'lg' }, // 기본 컨테이너 너비 설정
        },
    },
});
// 폰트 크기 자동 조절 (모바일에서 너무 크지 않게)
theme = responsiveFontSizes(theme);

const MainPage = () => {
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // MD(900px) 이하일 때 true
    const [mobileOpen, setMobileOpen] = useState(false); // 모바일 메뉴 토글 상태

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const navItems = ['Home', 'About', 'Service', 'Solution', 'Contact'];

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />

            {/* Mobile Drawer (Side Menu) */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{ '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 } }}
            >
                <List>
                    {navItems.map((item) => (
                        <ListItemButton key={item} onClick={handleDrawerToggle}>
                            <ListItemText primary={item} />
                        </ListItemButton>
                    ))}
                </List>
            </Drawer>

            {/* --- B. Hero Section --- */}
            <Box
                sx={{
                    height: { xs: '70vh', md: '600px' }, // 높이 조절
                    minHeight: '400px',
                    width: '100%',
                    backgroundImage: `url(${assets.heroBackground})`,
                    backgroundColor: '#f5f5f5',
                    backgroundSize: 'cover', // 이미지를 잘라서 채움
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(to right, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0) 100%)',
                    }}
                />

                <Container sx={{ position: 'relative', zIndex: 2 }}>
                    <Box
                        sx={{
                            color: '#fff',
                            textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                        }}
                    >
                        <Typography
                            variant={isMobile ? 'h4' : 'h2'}
                            sx={{
                                wordBreak: 'keep-all',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.2,
                            }}
                        >
                            사람을 대체하는 기술이 아니라,
                        </Typography>
                        <Typography
                            variant={isMobile ? 'h4' : 'h2'}
                            sx={{
                                wordBreak: 'keep-all',
                                fontWeight: 'bold',
                                letterSpacing: '-0.02em',
                                lineHeight: 1.2,
                                mb: 1,
                            }}
                        >
                            사람을 만나야 할 지점을 정확히 연결하는 기술
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            sx={{
                                mt: 3,
                                opacity: 0.95,
                                wordBreak: 'keep-all',
                                letterSpacing: '0.05em',
                                fontWeight: 300,
                            }}
                        >
                            가람마인드는 심리학과 AI 기술을 결합하여, 정신건강 위험을 조기에
                            포착하고
                            <br className="hidden md:block" />
                            지속적으로 관리할 수 있는 솔루션을 개발합니다.
                        </Typography>
                    </Box>
                </Container>
            </Box>

            {/* --- C. Reality Section (Cards) --- */}
            <Container maxWidth="lg" sx={{ py: { xs: 8, md: 15 } }}>
                <Box textAlign="center" mb={6}>
                    <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 400 }}>
                        마음의 위기 앞에 놓인
                    </Typography>
                    <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight="bold">
                        우리들의 현실
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 2, fontSize: '1.1rem' }}>
                        도움이 필요하지만 시작이 어려운 이들을 위해 만들었습니다.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr', // 모바일: 위에서 아래로 1개씩
                            md: 'repeat(3, 1fr)', // PC: 가로로 3개 균일하게
                        },
                        gap: { xs: 3, md: 4 }, // 간격: 모바일은 좁게(24px), PC는 넓게(32px)
                        alignItems: 'stretch', // 높이 통일
                    }}
                >
                    {[
                        {
                            title: '정신 건강 솔루션',
                            desc: '기술로 정밀하게, 데이터로 건강하게 제안합니다',
                            image: assets.card1,
                        },
                        {
                            title: '데이터로 증명하는 서비스',
                            desc: '기술을 넘어, 사용자의 경험까지 케어합니다.',
                            image: assets.card2,
                        },
                        {
                            title: '현장 심리 지원 서비스',
                            desc: '전문가의 심리 솔루션을 만나보세요.',
                            image: assets.card3,
                        },
                    ].map((item, idx) => (
                        <Grid item xs={12} md={4} key={idx} sx={{ display: 'flex' }}>
                            <Box
                                sx={{
                                    p: 4,
                                    width: '100%',
                                    height: '100%',
                                    minHeight: '380px',
                                    borderRadius: 5,
                                    boxShadow: 3, //그림자

                                    //텍스트 설정
                                    color: '#fff', //글자색
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-end', //글자 위치
                                    textAlign: 'left',

                                    //배경 설정
                                    backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 60%), url(${item.image})`,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    backgroundRepeat: 'no-repeat',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontWeight: 'bold',
                                        fontSize: '1.45rem',
                                        lineHeight: 1,
                                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                    }}
                                >
                                    {item.title}
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        mt: 0.5,
                                        fontWeight: 500,
                                        opacity: 0.9, // 너무 쨍한 흰색보다는 약간 투명하게
                                        wordBreak: 'keep-all',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                                    }}
                                >
                                    {item.desc}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Box>
                {/* </Grid> */}
            </Container>

            {/* --- D. Mallang Pocket Section --- */}
            <Box sx={{ bgcolor: '#f5f9fc' }}>
                <Container maxWidth="xl" sx={{ py: { xs: 8, md: 15 } }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                md: 'repeat(3, 1fr)',
                            },
                            gap: { xs: 3, md: 4 },
                            alignItems: 'stretch',
                        }}
                    >
                        <Grid item xs={12} md={6} pl={4} alignItems="center">
                            <Box sx={{ textAlign: { xs: 'left', md: 'left' } }}>
                                <Typography
                                    variant="h6"
                                    color="#045584"
                                    fontWeight="bold"
                                    gutterBottom
                                >
                                    MALLANG POCKET
                                </Typography>
                                <Typography sx={{ fontWeight: 'bold', fontSize: '2em', mb: 2 }}>
                                    말랑포켓
                                </Typography>
                                <Typography sx={{ fontWeight: 400 }}>
                                    말랑포켓은 일기, 감정 체크, 대화형 AI를 통해 사용자의 정서
                                    상태를 일상속에서 자연스럽게 기록하고 분석하는 솔루션입니다.
                                </Typography>

                                <Stack
                                    spacing={2}
                                    sx={{
                                        mt: 6,
                                        maxWidth: '280px',
                                        mx: { xs: 'auto', md: 0 },
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        href="https://play.google.com/store/apps/details?id=com.garammind.mallangpocket&pcampaignid=web_share"
                                        sx={{
                                            bgcolor: '#005580',
                                            borderRadius: 10,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        구글 다운로드
                                    </Button>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        href="https://apps.apple.com/kr/app/말랑포켓/id6755514501"
                                        sx={{
                                            bgcolor: '#005580',
                                            borderRadius: 10,
                                            py: 1.5,
                                            fontSize: '1.1rem',
                                        }}
                                    >
                                        애플 다운로드
                                    </Button>
                                </Stack>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={assets.appShowcase} // 미리 준비한 폰 이미지
                                    alt="App Showcase"
                                    sx={{ width: '100%', maxWidth: '380px', height: '400px' }}
                                />
                                <Box sx={{ width: '100%', maxWidth: '380px' }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ mt: 3, fontWeight: 'bold', textAlign: 'left' }}
                                    >
                                        단절 없는 마음 관리, CARE-Loop
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '2px',
                                            bgcolor: '#333',
                                            my: 2,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{ textAlign: 'left', color: 'text.secondary' }}
                                    >
                                        상담으로 끝나지 않고, 진단과 적절한 정보
                                        <br />
                                        연계까지 이어지는 선순환 구조를 완성합니다
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    height: '100%',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={assets.chart} // 차트/말풍선 이미지
                                    alt="Data Analysis"
                                    sx={{
                                        width: '90%',
                                        maxWidth: '380px',
                                        height: '400px',
                                    }}
                                />
                                <Box sx={{ width: '100%', maxWidth: '380px' }}>
                                    <Typography
                                        variant="h6"
                                        sx={{ mt: 3, fontWeight: 'bold', textAlign: 'left' }}
                                    >
                                        말 속에서 마음을 읽는 데이터 과학
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: '2px',
                                            bgcolor: '#333',
                                            my: 2,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        sx={{ textAlign: 'left', color: 'text.secondary' }}
                                    >
                                        말랑포켓은 대화 내용과 일상 기록을 분석하여
                                        <br />
                                        정서 행동 양상을 과학적으로 추론합니다
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Box>
                </Container>
            </Box>

            {/* --- E. Deep Tech Section --- */}
            <Container sx={{ py: { xs: 10, md: 15 } }}>
                <Box textAlign="center" mb={8}>
                    <Typography variant="h4" fontWeight={400}>
                        단순히 챗봇이 아닌
                    </Typography>
                    <Typography
                        variant={isMobile ? 'h5' : 'h4'}
                        fontWeight="bold"
                        gutterBottom
                        sx={{ wordBreak: 'keep-all' }}
                    >
                        과학적 근거에 기반한 딥테크 기술
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'repeat(3, 1fr)',
                        },
                        gap: { xs: 4, md: 6 },
                        alignItems: 'stretch',
                        textAlign: 'left',
                    }}
                >
                    {[
                        {
                            icon: assets.icon1,
                            title: 'MI(동기면담) 기반 AI',
                            desc: '과학적으로 검증된 동기면담 기법을 LLM에 이식하여 정서적 공감과 동기를 이끌어냅니다.',
                        },
                        {
                            icon: assets.icon2,
                            title: '정서행동 예측 모델',
                            desc: '자연어 분석을 통해 우울, 불안, 스트레스 지표를 정량화하고 증상을 범주화합니다.',
                        },
                        {
                            icon: assets.icon3,
                            title: '개인정보 보호',
                            desc: '온디아비스 AI 및 연합학습(Federated Learning) 기술을 통해 사용자의 데이터를 안전하게 보호합니다.',
                        },
                    ].map((item, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box
                                component="img" // 이미지를 바로 렌더링
                                src={item.icon}
                                alt={item.title}
                                sx={{
                                    width: 56, // 아이콘 크기 (약간 키움)
                                    height: 56,
                                    mb: 2.5, // 제목과의 간격
                                    objectFit: 'contain', // 비율 유지
                                }}
                            />

                            {/* 제목 */}
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                gutterBottom
                                sx={{ fontSize: '1.25rem' }}
                            >
                                {item.title}
                            </Typography>

                            {/* 설명 */}
                            <Typography
                                variant="body1"
                                sx={{
                                    lineHeight: 1.7,
                                    wordBreak: 'keep-all', // 한글 단어 끊김 방지
                                    fontSize: '0.95rem',
                                    opacity: 0.8,
                                }}
                            >
                                {item.desc}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Container>

            {/* --- F. Vision & Team Section --- */}
            <Container maxWidth="xl" sx={{ pb: { xs: 8, md: 15 }, px: { xs: 3, md: 5 } }}>
                {/* 1. 상단 이미지 영역 - 중앙 정렬을 위해 Grid 대신 별도 Box 사용 */}
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        mb: { xs: 5, md: 8 },
                    }}
                >
                    <Box
                        component="img"
                        src={assets.teamMeeting}
                        alt="Team Meeting"
                        sx={{
                            width: '80%', // 컨테이너 너비에 맞춤
                            height: { xs: '300px', md: '550px' }, // 고정 높이를 주어 크롭 효과 유지
                            borderRadius: 4,
                            boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                            objectFit: 'cover',
                            objectPosition: 'center',
                        }}
                    />
                </Box>

                {/* 2. 하단 텍스트 영역 - 양 옆으로 갈라지는 구조 */}
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between', // 🌟 좌우 끝으로 밀어내기
                            alignItems: 'flex-start',
                            width: '80%',
                        }}
                    >
                        {/* (1) 왼쪽: 파란색 소제목 */}
                        <Box sx={{ mb: { xs: 2, md: 0 } }}>
                            <Typography
                                sx={{
                                    color: '#006699',
                                    fontWeight: 800,
                                    fontSize: '1.3rem',
                                    letterSpacing: '-0.5px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                We created this for you
                            </Typography>
                        </Box>

                        {/* (2) 오른쪽: 메인 제목 및 설명 */}
                        <Box
                            sx={{
                                textAlign: { xs: 'left', md: 'right' },
                                maxWidth: { md: '70%' }, // 텍스트 영역 너비 확보
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: { xs: 'flex-start', md: 'flex-end' },
                            }}
                        >
                            <Typography
                                variant={isMobile ? 'h4' : 'h3'}
                                sx={{
                                    fontWeight: 800,
                                    color: '#111',
                                    lineHeight: 1.3,
                                    mb: 3,
                                    wordBreak: 'keep-all',
                                }}
                            >
                                가람마인드는 마음 건강을 지킵니다.
                            </Typography>

                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#555',
                                    fontSize: { xs: '1rem', md: '1.125rem' },
                                    lineHeight: 1.8,
                                    wordBreak: 'keep-all',
                                    fontWeight: 500,
                                }}
                            >
                                가람마인드는 AI 기술과 임상심리학을 결합하여,{' '}
                                <Box component="span" sx={{ fontWeight: 800, color: '#000' }}>
                                    정신건강의 사각지대를
                                    <Box
                                        component="br"
                                        sx={{ display: { xs: 'none', md: 'block' } }}
                                    />
                                    ‘발견하고, 연결하고, 지속적으로 돌보는’
                                </Box>{' '}
                                솔루션과 서비스를 제공합니다.
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </ThemeProvider>
    );
};

export default MainPage;
